import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';

import * as St from './SidePanelSectionTab.styles';
import { useSidePanelSectionTab, useSidePanelSectionTabArgs } from './hooks';

import { DesignSection } from '~/core/enums';
import { useGetDesignSectionTranslations } from '~/core/hooks/useGetDesignSectionTranslations';
import ItemCountAdjustButton from '~atoms/ItemCountAdjustButton';
import DesignStepContainer from '~molecules/DesignStepContainer';
import DesignOptionIn3DModal from '~organisms/DesignOptionIn3DModal';
import DesignSectionCard from '~organisms/DesignSectionCard';
import DesignSectionHeader from '~organisms/DesignSectionHeader';

type SidePanelSectionTabProps<T> = {
  section: DesignSection;
  selectOptionText?: string;
  modal3DButtonText?: string;
  removeModalTitle?: (args: { selectedOptionId?: string | number }) => string;
  removeModalDescription?: (args: {
    selectedOptionId?: string | number;
  }) => string;
  getWarningMsg?: (optionTitle: string) => string;
  hookArgs: useSidePanelSectionTabArgs<T>;
  hidePrice?: boolean;
};

const SidePanelStepContainer = <T,>({
  getId,
  countersLimit,
  hookArgs,
  hidePrice,
  modal3DButtonText,
  selectOptionText,
}: {
  getId: useSidePanelSectionTabArgs<T>['getId'];
  countersLimit: useSidePanelSectionTabArgs<T>['countersLimit'];
  hookArgs: ReturnType<typeof useSidePanelSectionTab<T>>;
  hidePrice?: boolean;
  modal3DButtonText?: string;
  selectOptionText: string;
}) => {
  const {
    options,
    modalFor3DCarousel,
    getQty,
    prepareToAdd,
    prepareToRemove,
    optionsById,
    selectedOptionId,
    setSelectedOptionId,
  } = hookArgs;

  return (
    <DesignStepContainer>
      {options.map((option, index) => {
        const optionId = getId(option);
        const qty = getQty(optionId);
        const display = optionsById[optionId].display;
        const addOption = prepareToAdd(optionId, qty);
        const isSelected = selectedOptionId === optionId;

        return (
          <DesignSectionCard
            key={optionId}
            title={display.title}
            image={display.image}
            onClick3DBtn={
              modal3DButtonText
                ? () => modalFor3DCarousel.setOptionIndex(index)
                : undefined
            }
            isSelected={isSelected}
            onClick={() => {
              setSelectedOptionId(optionId);
              addOption?.();
            }}
            tooltipText={
              qty < (countersLimit ?? 0) ? selectOptionText : undefined
            }
            isDisabled={display.isDisabled}
          >
            {hidePrice ? null : <p>{display.price}</p>}
            <ItemCountAdjustButton
              quantity={isSelected || qty > 0 ? qty : undefined}
              onDecrease={prepareToRemove(optionId)}
              onIncrease={addOption}
              isDisabled={display.isDisabled}
            />
          </DesignSectionCard>
        );
      })}
    </DesignStepContainer>
  );
};

const SidePanelSectionTab = <T,>({
  section,
  selectOptionText,
  removeModalTitle,
  removeModalDescription,
  modal3DButtonText,
  hookArgs,
  hidePrice,
}: SidePanelSectionTabProps<T>) => {
  const { t } = useTranslation();
  const { getId } = hookArgs;
  const sidePanelHookData = useSidePanelSectionTab(hookArgs);
  const {
    isFetching,
    options,
    modalFor3DCarousel,
    modalForOptionRemoval,
    getQty,
    prepareToAdd,
    optionsById,
    selectedOptionId,
  } = sidePanelHookData;

  const { title, description, option } = useGetDesignSectionTranslations(
    section,
    ['OPTION']
  );

  const defaultSelectOptionText = t('DESIGN_TOOLBAR.SELECT', {
    option,
  });
  const defaultRemoveModalTitle = t('DESIGN_TOOLBAR.REMOVE_MODAL.TITLE', {
    option,
  });
  const defaultRemoveModalDescription = t(
    'DESIGN_TOOLBAR.REMOVE_MODAL.DESCRIPTION',
    {
      option,
    }
  );

  return isFetching ? (
    <Spin style={{ fontSize: '24rem' }} />
  ) : (
    <>
      <DesignSectionHeader
        title={title}
        description={description}
        subtitleStyle={{ fontSize: '16rem' }}
      />

      {options.length > 0 && (
        <SidePanelStepContainer
          getId={getId}
          countersLimit={hookArgs.countersLimit}
          modal3DButtonText={modal3DButtonText}
          hookArgs={sidePanelHookData}
          hidePrice={hidePrice}
          selectOptionText={selectOptionText ?? defaultSelectOptionText}
        />
      )}

      {modal3DButtonText && options.length > 0 && (
        <DesignOptionIn3DModal
          options={options.map((op) => optionsById[getId(op)])}
          optionIndex={modalFor3DCarousel.optionIndex}
          isOpen={modalFor3DCarousel.isOpen}
          onClose={modalFor3DCarousel.close}
          selectOption={(opt, optId = getId(opt)) =>
            prepareToAdd(optId, getQty(optId))?.()
          }
          section={section}
          buttonText={modal3DButtonText}
        />
      )}

      <St.RemoveOptionModal
        open={modalForOptionRemoval.isOpen}
        onCancel={modalForOptionRemoval.close}
        footer={
          <St.RemoveOptionModalCloseBtn onClick={modalForOptionRemoval.close}>
            Close
          </St.RemoveOptionModalCloseBtn>
        }
      >
        <St.RemoveOptionModalTitle>
          {removeModalTitle
            ? removeModalTitle({ selectedOptionId })
            : defaultRemoveModalTitle}
        </St.RemoveOptionModalTitle>
        <St.RemoveOptionModalDescription>
          {removeModalDescription
            ? removeModalDescription({ selectedOptionId })
            : defaultRemoveModalDescription}
        </St.RemoveOptionModalDescription>
      </St.RemoveOptionModal>
    </>
  );
};

export default SidePanelSectionTab;
